import React, { useEffect, useMemo } from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import usePartnerApi from "hooks/api/private/usePartnerApi";
import { usePartnerId } from "hooks/useId";
import useSingleFile from "hooks/useSingleFile";

import { PartnerBody, PartnerDetails } from "types/api/partner";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { imageNotEmpty, getExtraErrorsDescription, urlValidator } from "utils/validators";

export type PartnerFormType = {
    id?: string;
    enabled: boolean;
    link: string;
    logo: AttachmentType;
    text: {
        it: {
            title: string;
        };
        en: {
            title: string;
        };
    };
};

export type PartnerFormHook = {
    setSingleFile: (fileToSet: AttachmentType, path: string) => void;
    formHook: FormHookType<PartnerFormType>;
};

function usePartnerForm(): PartnerFormHook {
    const partnerId = usePartnerId();

    const { createPartner, updatePartner, getOnePartner } = usePartnerApi();

    const schema = useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                id: { type: "simple", validators: [] },
                enabled: { type: "simple", validators: [] },
                link: { type: "simple", validators: [notEmptyValidator, urlValidator] },
                logo: imageNotEmpty,
                text: {
                    type: "object",
                    properties: {
                        it: {
                            type: "object",
                            properties: {
                                title: { type: "simple", validators: [notEmptyValidator] },
                            },
                        },
                        en: {
                            type: "object",
                            properties: {
                                title: { type: "simple", validators: [] },
                            },
                        },
                    },
                },
            },
        }),
        [],
    );

    const initialValues: PartnerFormType = React.useMemo(
        () => ({
            enabled: true,
            link: "",
            logo: createAttachmentType({ label: "Logo" }),
            text: {
                it: { title: "" },
                en: { title: "" },
            },
        }),
        [],
    );

    const formHook = useForm<PartnerFormType>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    const [partnerDetails, setPartnerDetails] = React.useState<PartnerDetails | undefined>(undefined);

    React.useEffect(() => {
        if (partnerId) {
            const [apiCall, abortController] = getOnePartner({ id: partnerId });

            apiCall.then(setPartnerDetails);

            return () => { abortController.abort(); };
        }
    }, [getOnePartner, partnerId]);

    React.useEffect(() => {
        if (partnerDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = partnerDetails.id;
                newForm.enabled = partnerDetails.enabled;
                newForm.link = partnerDetails.link ?? "";
                newForm.text.it.title = partnerDetails.text.it?.title ?? "";
                newForm.text.en.title = partnerDetails.text.en?.title ?? "";

                if (partnerDetails.logo) {
                    newForm.logo = createAttachmentType({
                        id: partnerDetails.logo.id,
                        label: "Logo",
                        filename: partnerDetails.logo.filename,
                        status: 2,
                    });
                }

                return newForm;
            });
        }
    }, [setForm, partnerDetails]);

    const { addSaveAction } = useSaveActionsContext();

    useEffect(() => {
        addSaveAction("usePartnerForm", () => {
            if (isValid) {
                const body: PartnerBody = {
                    enabled: form.enabled,
                    link: form.link,
                    logo: converterAttachmentToFileWithPayload(form.logo),
                    text: {
                        it: {
                            title: form.text.it.title,
                        },
                        en: {
                            title: form.text.en.title,
                        },
                    },
                };

                if (partnerId) {
                    const [apiCall] = updatePartner({ id: partnerId }, body);
                    return apiCall;
                } else {
                    const [apiCall] = createPartner(body);
                    return apiCall;
                }
            } else {
                showAllError();
                return Promise.reject(new Error("INVALID_FORM"));
            }
        });
    }, [addSaveAction, showAllError, createPartner, updatePartner, isValid, partnerId, form]);

    const { setSingleFile } = useSingleFile({ setForm });

    return React.useMemo(
        (): PartnerFormHook => ({
            setSingleFile,
            formHook,
        }),
        [setSingleFile, formHook],
    );
}

export default usePartnerForm;
